import { Sparkles } from "lucide-react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatWallClockDate } from "@/lib/format";
import { RELEASE_NOTES } from "@/lib/release-notes";
import { cn } from "@/lib/utils";

// =============================================================================
// WhatsNewDialog
//
// Opened from the version number in the footer (see AppFooter). Shows a
// scrollable, newest-first list of releases written in plain language
// for non-technical hospital staff — the friendly counterpart to the
// technical root CHANGELOG.md. The copy lives in
// `@/lib/release-notes`; this component is purely presentation.
//
// The entry whose version matches the running build (`__APP_VERSION__`)
// is tagged "Current" so a user can see at a glance which version
// they're on relative to the history.
// =============================================================================

export interface WhatsNewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WhatsNewDialog({ open, onOpenChange }: WhatsNewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]" structured>
        <DialogHeader icon={Sparkles}>
          <DialogTitle>What’s new</DialogTitle>
          <DialogDescription>
            A plain-language rundown of recent updates to the MSMC Portal.
          </DialogDescription>
        </DialogHeader>

        {/* The body scrolls, so a long history stays inside the pop-up
            rather than pushing it past the viewport on a tablet. It takes
            focus, so the keyboard can scroll it: the X is the only other
            stop. */}
        <DialogBody
          role="region"
          aria-label="Release notes"
          tabIndex={0}
          className="focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
        >
          <ol className="space-y-4">
            {RELEASE_NOTES.map((note) => {
              const isCurrent = note.version === __APP_VERSION__;
              const bullets = Array.isArray(note.summary)
                ? note.summary
                : [note.summary];
              return (
                <li
                  key={note.version}
                  className={cn(
                    "rounded-xl border p-3.5",
                    isCurrent
                      ? "border-primary/55 bg-primary/[0.06]"
                      : "border-border bg-foreground/[0.02]",
                  )}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-mono text-sm font-bold text-foreground">
                      v{note.version}
                    </span>
                    {isCurrent ? (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-blue-700 dark:text-primary">
                        Current
                      </span>
                    ) : null}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {formatWallClockDate(note.date)}
                    </span>
                  </div>

                  {bullets.length === 1 ? (
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {bullets[0]}
                    </p>
                  ) : (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground marker:text-foreground/40">
                      {bullets.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
